import type { paths } from '@luxuryescapes/contract-svc-lux-loyalty'
import { isValidDate } from 'lib/datetime/dateUtils'

type EnrollmentProgressResponse = paths['/api/lux-loyalty/claims/campaigns/{campaignId}/enrollment-progress']['get']['responses']['200']['content']['application/json']
type EnrollResponse = paths['/api/lux-loyalty/claims/campaigns/{campaignId}/enroll']['post']['responses']['200']['content']['application/json']

export type StatusMatchEnrollmentProgress = EnrollmentProgressResponse['result']
export type StatusMatchEnrollmentInfo = EnrollResponse['result']['enrollment']

export type StatusMatchEnrollResult = EnrollResponse['result'] & {
  /** Set by the FE modal — the program the user selected */
  selectedProgram?: string
  /** Set by the FE modal — the source tier the user selected */
  selectedTier?: string
}

export interface StatusMatchProgram {
  name: string
  /** Cloudinary image id. When omitted, the program name is shown instead. */
  logoImageId?: string
  tiers: Array<{
    sourceTier: string
    targetSocieteTier: 'Silver' | 'Gold'
  }>
}

/**
 * Evidence config variants — determines the application modal form layout.
 * Discriminated by `type`. Add new variants here as new modal types are introduced.
 */
export type StatusMatchEvidence = ProgramTierPhotosEvidence

/**
 * Variant: 2 cascading dropdowns (program → tier) + photo upload.
 * The `fieldNames` object maps our predefined field roles to the BE's
 * evidenceFields[].name values (which become keys in the submitted `evidence` JSON).
 */
export interface ProgramTierPhotosEvidence {
  type: 'programTierPhotos'
  fieldNames: {
    program: string
    tier: string
    photo: string
  }
  verificationImageId?: string
  minPhotos: number
  photoHints: Array<string>
  acceptedFormats: Array<string>
  proofRequirements: Array<string>
}

export const STATUS_MATCH_EVIDENCE_TYPES: ReadonlyArray<StatusMatchEvidence['type']> = ['programTierPhotos']

export interface StatusMatchHero {
  title: string
  subtitle: string
  description: string
  ctaText: string
  imageId: string
}

export interface StatusMatchApplicationPeriod {
  startsAt: string
  endsAt: string
}

export interface StatusMatchFaq {
  question: string
  answer: string
}

export interface StatusMatchCondition {
  icon: 'clock' | 'calendar' | 'star' | 'lock'
  title: string
  description: string
}

export interface StatusMatchHowItWorksStep {
  title: string
  description: string
}

export interface StatusMatchSuccessStep {
  /** Matches the BE's matchCheckConfig.checks[].key — used to pair with eligible status */
  key: string
  title: string
  description: string
}

export type ChallengeCardBackground =
  | { type: 'tierGradient' }
  | { type: 'image'; imageId: string }
  | { type: 'color'; value: string }

export interface ChallengeCardConfig {
  background: ChallengeCardBackground
  /** Template with {targetTier}, {expiresDate} placeholders */
  cardTitle: string
  /** Template with {targetTier}, {expiresDate} placeholders */
  cardSubtitle: string
  /** Template with {targetTier}, {expiresDate} placeholders */
  cardDescription?: string
}

export interface StatusMatchLabPayload {
  slug: string
  campaignId: string
  allowedRegions: Array<string>
  /** When true, all Apply/CTA buttons are disabled and application sections are hidden */
  restrictApplications?: boolean
  /** Campaign application window */
  applicationPeriod: StatusMatchApplicationPeriod
  /** Warning banner shown at the top of the page (e.g. when campaign has expired) */
  expiredBanner?: {
    message: string
  }
  hero: StatusMatchHero
  programs: Array<StatusMatchProgram>
  evidence: StatusMatchEvidence
  conditions: Array<StatusMatchCondition>
  howItWorks?: Array<StatusMatchHowItWorksStep>
  successSteps?: Array<StatusMatchSuccessStep>
  challengeCard?: ChallengeCardConfig
  faqs: Array<StatusMatchFaq>
  terms: Array<string>
  termsSlug: string
}

export interface StatusMatchCampaignConfig {
  config: StatusMatchLabPayload
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0
}

function isKnownEvidenceType(value: unknown): value is StatusMatchEvidence['type'] {
  return typeof value === 'string' && (STATUS_MATCH_EVIDENCE_TYPES as ReadonlyArray<string>).includes(value)
}

function isValidEvidence(value: unknown): value is StatusMatchEvidence {
  if (!isObject(value)) return false
  if (!isKnownEvidenceType(value.type)) return false

  if (value.type === 'programTierPhotos') {
    return (
      isObject(value.fieldNames) &&
      isNonEmptyString(value.fieldNames.program) &&
      isNonEmptyString(value.fieldNames.tier) &&
      isNonEmptyString(value.fieldNames.photo) &&
      typeof value.minPhotos === 'number' &&
      Array.isArray(value.photoHints) &&
      Array.isArray(value.acceptedFormats) &&
      Array.isArray(value.proofRequirements)
    )
  }

  return false
}

export function isStatusMatchLabPayload(
  payload: unknown,
): payload is StatusMatchLabPayload {
  if (!isObject(payload)) return false
  return (
    isNonEmptyString(payload.slug) &&
    isNonEmptyString(payload.campaignId) &&
    Array.isArray(payload.allowedRegions) &&
    isObject(payload.applicationPeriod) &&
    isNonEmptyString(payload.applicationPeriod.startsAt) &&
    isNonEmptyString(payload.applicationPeriod.endsAt) &&
    isValidDate(new Date(payload.applicationPeriod.startsAt)) &&
    isValidDate(new Date(payload.applicationPeriod.endsAt)) &&
    isObject(payload.hero) &&
    isNonEmptyString(payload.hero.title) &&
    isNonEmptyString(payload.hero.imageId) &&
    Array.isArray(payload.programs) &&
    payload.programs.length > 0 &&
    isValidEvidence(payload.evidence) &&
    Array.isArray(payload.faqs) &&
    Array.isArray(payload.terms) &&
    isNonEmptyString(payload.termsSlug)
  )
}
